import { z } from 'zod'
import { create } from 'zustand'

import {
  trackDetails,
  trackLessonList,
  trackQuiz,
} from '@/services/track/track.gql'

import { queryClient } from '@/lib/tanstack-query'
import { JSONParseSafe } from '@/lib/utils'

import { ILessonItem } from './type'

type Value = {
  trackId: string
  lessonId: string
  lessonsList: ILessonItem[]
  lessonsCount: number
  selected: ILessonItem
  lesson: {
    title: string
  }
  chapterTitle: string
  status: 'idle' | 'running' | 'finished' | 'timeout'
}

type Action = {
  /**
   * Setup parsed questions
   */
  setup: (...args: Parameters<typeof parseDataToSetup>) => Promise<void>
  onChangeLesson: (idx: number) => void
  reset: () => void
}

const initialValue: Value = {
  trackId: '',
  lessonId: '',
  lessonsList: [],
  lessonsCount: 0,
  selected: null!,
  lesson: { title: '' },
  chapterTitle: '',
  status: 'idle',
}

const useTaskLesson = create<Action & Value>((set, get) => ({
  ...initialValue,
  // Action
  async setup(trackId, lessonId) {
    const result = await parseDataToSetup(lessonId, trackId)
    set({ ...result, trackId })
  },
  onChangeLesson(idx) {
    const { lessonsList, selected: oldSelected } = get()

    if (oldSelected) {
      const oldIndex = lessonsList.findIndex((q) => q.id === oldSelected.id)
      if (oldIndex !== -1) {
        lessonsList[oldIndex] = oldSelected
      }
    }

    const selected = lessonsList[idx] || null!
    if (!selected) {
      return
    }

    set({
      lessonsList,
      selected,
      lesson: { title: selected.title },
    })
  },
  reset() {
    set({ ...initialValue })
  },
}))

async function parseDataToSetup(
  lessonId: string,
  trackId: string
): Promise<
  Pick<
    Value,
    | 'trackId'
    | 'lessonId'
    | 'lessonsList'
    | 'lessonsCount'
    | 'lesson'
    | 'selected'
    | 'chapterTitle'
  >
> {
  const [track, lesson] = await Promise.all([
    queryClient.ensureQueryData(trackDetails.qry({ trackId, myTrack: true })),
    queryClient.ensureQueryData(trackLessonList.qry({ chapterId: lessonId })),
  ])

  const lessonsList: ILessonItem[] = lesson.listLessonFromDetailChapter.lessons
    .map((x, i) => {
      if (!x) return null
      return {
        id: x.id,
        index: i,
        title: x.title,
        content: x.content,
        lessonContent: JSONParseSafe(
          x.lesson_content
        ) as ILessonItem['lessonContent'],
        imageUrl: x.image_url,
        type: x.type,
        lessonType: x.lesson_type,
        // type: x.type,
        status: x.solved_lesson?.completed_at ? 'correct' : 'idle',
      } satisfies ILessonItem
    })
    .filter(Boolean)

  const lessonsCount = lessonsList.length
  const selected = lessonsList[0]!

  let chapterTitle = ''
  track.getListTutorialFromDetailTrack.tutorials.find((t) => {
    t?.chapters?.find((c) => {
      const select = c?.id === lessonId
      if (select) {
        chapterTitle = c.title
      }
    })
  })

  if (lessonsCount === 0) throw new Error('Lesson not found')

  return {
    trackId,
    lessonId,
    lessonsList,
    lessonsCount,
    selected: selected,
    lesson: { title: '' },
    chapterTitle,
  }
}

export { useTaskLesson }
